import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';


import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, forkJoin, concat } from 'rxjs';
import { takeUntil, toArray } from 'rxjs/operators';

import { CalendarService } from '../../../services/calendar.service';
import { AuthService } from '../../../services/auth.service';
import { InteractionsService } from '../../../services/interactions.service';
import { CalendarVisibilityEntry } from '../../../interfaces/calendar-event.interface';
import { User } from '../../../interfaces/auth.interface';

@Component({
  selector: 'app-calendar-visibility-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatTooltipModule
],
  templateUrl: './calendar-visibility-dialog.component.html',
  styleUrls: ['./calendar-visibility-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarVisibilityDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isLoading = true;
  isSaving = false;
  hasChanges = false;

  users: User[] = [];
  existingRules: CalendarVisibilityEntry[] = [];

  /**
   * Mátrix: visibilityMap[viewerUserId][targetUserId] = true/false
   * true = a viewer látja a target naptárát
   */
  visibilityMap: Record<number, Record<number, boolean>> = {};
  originalMap: Record<number, Record<number, boolean>> = {};

  constructor(
    private dialogRef: MatDialogRef<CalendarVisibilityDialogComponent>,
    private calendarService: CalendarService,
    private authService: AuthService,
    private interactionsService: InteractionsService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.isLoading = true;

    forkJoin({
      users: this.authService.getAllUsers(),
      visibility: this.calendarService.getAllVisibility(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ users, visibility }) => {
          this.users = users || [];
          this.existingRules = visibility.rules || [];
          this.buildVisibilityMap();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Adatok betöltése sikertelen:', err);
          this.isLoading = false;
          this.cdr.markForCheck();
          this.snackBar.open('Hiba az adatok betöltésekor', 'OK', { duration: 3000 });
        },
      });
  }

  private buildVisibilityMap(): void {
    const map: Record<number, Record<number, boolean>> = {};

    for (const user of this.users) {
      map[user.id] = {};
      for (const target of this.users) {
        if (user.id === target.id) continue;
        map[user.id][target.id] = false;
      }
    }

    for (const rule of this.existingRules) {
      if (map[rule.viewer_user_id]) {
        map[rule.viewer_user_id][rule.target_user_id] = true;
      }
    }

    this.visibilityMap = map;
    this.originalMap = JSON.parse(JSON.stringify(map));
  }



  isChecked(viewerId: number, targetId: number): boolean {
    return this.visibilityMap[viewerId]?.[targetId] || false;
  }

  toggleVisibility(viewerId: number, targetId: number): void {
    if (!this.visibilityMap[viewerId]) this.visibilityMap[viewerId] = {};
    this.visibilityMap[viewerId][targetId] = !this.visibilityMap[viewerId][targetId];
    this.hasChanges = true;
  }

  onSave(): void {
    this.isSaving = true;
    const toGrant: { viewer: number; target: number }[] = [];
    const toRevoke: { viewer: number; target: number }[] = [];

    for (const viewerId of Object.keys(this.visibilityMap).map(Number)) {
      for (const targetId of Object.keys(this.visibilityMap[viewerId]).map(Number)) {
        const current = this.visibilityMap[viewerId][targetId];
        const original = this.originalMap[viewerId]?.[targetId] || false;

        if (current && !original) {
          toGrant.push({ viewer: viewerId, target: targetId });
        } else if (!current && original) {
          toRevoke.push({ viewer: viewerId, target: targetId });
        }
      }
    }

    if (toGrant.length === 0 && toRevoke.length === 0) {
      this.dialogRef.close(false);
      return;
    }

    // Szekvenciálisan futtatjuk a kéréseket, hogy ne merítsük ki a backend DB pool-t
    const operations = [
      ...toGrant.map(g => this.calendarService.grantVisibility(g.viewer, g.target)),
      ...toRevoke.map(r => this.calendarService.revokeVisibility(r.viewer, r.target)),
    ];

    concat(...operations)
      .pipe(
        toArray(),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          // Naptár hozzáférés változások logolása az interactions táblába
          const changes = [
            ...toGrant.map(g => ({
              viewer_user_id: g.viewer,
              viewer_username: this.users.find(u => u.id === g.viewer)?.name || String(g.viewer),
              target_user_id: g.target,
              target_username: this.users.find(u => u.id === g.target)?.name || String(g.target),
              action: 'granted',
            })),
            ...toRevoke.map(r => ({
              viewer_user_id: r.viewer,
              viewer_username: this.users.find(u => u.id === r.viewer)?.name || String(r.viewer),
              target_user_id: r.target,
              target_username: this.users.find(u => u.id === r.target)?.name || String(r.target),
              action: 'revoked',
            })),
          ];

          this.interactionsService.createInteraction({
            status: 'Naptár hozzáférés',
            affected_entities: { changes },
          } as any).pipe(takeUntil(this.destroy$)).subscribe();

          this.isSaving = false;
          this.snackBar.open(
            `Láthatóság frissítve (${toGrant.length} engedélyezve, ${toRevoke.length} visszavonva)`,
            'OK',
            { duration: 3000 }
          );
          this.dialogRef.close(true);
        },
        error: (err) => {
          console.error('Mentés sikertelen:', err);
          this.isSaving = false;
          this.cdr.markForCheck();
          this.snackBar.open('Hiba a mentéskor', 'OK', { duration: 3000 });
        },
      });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
